import { useState, useMemo, useRef, useEffect } from "react";
import { Stack, TextInput, ScrollArea, Group } from "@mantine/core";
import { IconCalendar, IconSearch, IconX } from "@tabler/icons-react";
import { TripResponse, VehicleTripResponse } from "types/trip.types";
import { VehicleTripCard } from "./VehicleTripCard";
import { ActionFunc } from "./helper";
import EventRegistry from "utils/eventRegistry";
import { DateInput, DateValue } from "@mantine/dates";
import dayjs from "dayjs";
import { UnassignedTripsCard } from "./UnassignedTripCard";
import DatePickerWithShift from "components/DatePickerWithShift";

interface TripsListPanelProps {
  vehicleTrips: VehicleTripResponse[];
  unassignedTrips?: TripResponse[];
  currentTripId?: number;
  currentUnassignedTripId?: number;
  timezone?: string;
  onAction?: ActionFunc;
  eventRegistry?: EventRegistry;
  date?: Date;
  adminMode?: boolean;
  loading?: boolean;
}

export default function TripsListPanel({
  vehicleTrips,
  unassignedTrips,
  timezone,
  currentTripId,
  currentUnassignedTripId,
  onAction,
  date,
  adminMode,
  loading,
}: TripsListPanelProps) {
  const [search, setSearch] = useState("");

  const filteredTrips = useMemo(() => {
    const s = search.toLowerCase();
    return vehicleTrips.filter((t) => {
      if (!s) return true;
      // t.externalId?.toLowerCase().includes(s) ||
      // t.courier?.firstName?.toLowerCase().includes(s) ||
      // t.courier?.lastName?.toLowerCase().includes(s) ||
      return (
        t.vehicle?.licensePlate?.toLowerCase().includes(s) ||
        `${t.vehicleTripId}` == s
      );
    });
  }, [search, vehicleTrips]);
  const filteredUnassignedTrips = useMemo(() => {
    const s = search.toLowerCase();
    if (!unassignedTrips) return [];
    return unassignedTrips.filter((t) => {
      if (!s) return true;
      // t.externalId?.toLowerCase().includes(s) ||
      // t.courier?.firstName?.toLowerCase().includes(s) ||
      // t.courier?.lastName?.toLowerCase().includes(s) ||
      return (
        t.vehicle?.licensePlate?.toLowerCase().includes(s) ||
        `${t.tripId}` == s ||
        t.externalId?.toLowerCase().includes(s)
      );
    });
  }, [search, unassignedTrips]);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  function scrollToItem(tripId: number) {
    const key = String(tripId);
    const node = itemRefs.current[key];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  function handleTripCardClick(tripId: number): void {
    onAction && onAction("set-current-trip", { tripId });
  }

  useEffect(() => {}, [date]);

  useEffect(() => {
    if (currentTripId) scrollToItem(currentTripId);
  }, [currentTripId]);
  const handleDateChange = (e: DateValue) => {
    e &&
      onAction &&
      onAction("date-change", {
        date: e,
      });
  };
  return (
    <Stack gap="xs" p={16} style={{ height: "100%", width: "100%" }}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "rgba(30,30,30,0.95)",
          paddingBottom: 8,
        }}
      >
        <Stack gap="xs">
          <DatePickerWithShift
            value={dayjs(date).toDate()}
            onChange={handleDateChange}
            // leftSection={<IconCalendar size={16} />}
            disabled={loading}
          />
          <TextInput
            placeholder="Search trips..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            rightSection={
              search ? (
                <IconX
                  size={16}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                />
              ) : null
            }
          />
        </Stack>
      </div>
      <ScrollArea h="100%" w="100%" scrollbars="y">
        <Stack gap="md">
          {filteredTrips.map((vehicleTrip) => (
            <div
              key={vehicleTrip.vehicleTripId}
              ref={(el) => {
                const key = String(vehicleTrip.vehicleTripId);
                itemRefs.current[key] = el;
              }}
            >
              <VehicleTripCard
                vehicleTrip={vehicleTrip}
                timezone={timezone}
                onClick={handleTripCardClick}
                onAction={onAction}
                current={vehicleTrip.vehicleTripId === currentTripId}
                adminMode={adminMode}
              />
            </div>
          ))}
          {Boolean(filteredUnassignedTrips?.length) && (
            <div
              key={"unassigned-trips"}
              ref={(el) => {
                const key = "unassigned-trips";
                itemRefs.current[key] = el;
              }}
            >
              <UnassignedTripsCard
                trips={filteredUnassignedTrips}
                onAction={onAction}
                onClick={() => handleTripCardClick(-1)}
                currentTripId={currentUnassignedTripId}
                expanded={currentTripId === -1}
                adminMode={adminMode}
              />
            </div>
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
